import React, { useMemo } from 'react';
import {
  CommunityApp,
  CommunityNavigation,
  MenuItemWithAnalytics,
  LinkType,
  NavbarSlideMenuItem,
  Icon,
  NavBarExpandableItem,
} from '@atlassian/learning-components';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useLocation } from 'react-router-dom';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getLoginUrl, getLogoutUrl, getSignUpUrl, getSwitchAccountUrl, getUrlPrefix } from 'src/services/http/user';
import { sendMarketingUIEvent } from 'src/utils/analytics';
import {
  LandingType,
  ContentHubType,
  CHAMPIONS_PAGE_FF,
  COMMUNITY_ACCOUNT_SWITCHER_FF,
  NEW_TOP_NAVIGATION_FF,
} from 'src/constants';
import { getHubUrl, getCertificationUrl, getCatalogUrl, getLandingUrl, getMyLearningUrl } from 'src/utils/getUrl';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';

import { OnDemandMobileMenu } from './OnDemandMobileMenu';

const LINKS: { [key: string]: LinkType } = {
  HOME: { text: 'Home', href: '/' },
  CATALOG: { text: 'Search catalog', href: getCatalogUrl() },
  BROWSE_ALL: { text: 'Browse all learning', href: getCatalogUrl() },
  LEARNING_PATHS: {
    text: 'Learning paths',
    href: getHubUrl(ContentHubType.LEARNING_PATHS),
  },
  GROW_YOUR_CAREER: {
    text: 'Grow your career',
    href: getHubUrl(ContentHubType.ROLE_BASED),
  },
  PREP_FOR_CERT: {
    text: 'Prepare for a certification',
    href: getHubUrl(ContentHubType.CERT_PREP),
  },
  LIVE_TRAINING: {
    text: 'Live training',
    href: getLandingUrl(LandingType.LIVE_TRAINING),
  },
  CERTIFICATIONS: {
    text: 'Certifications',
    href: getCertificationUrl(),
  },
  MY_LEARNING: {
    text: 'My learning',
    href: getMyLearningUrl(MyLearningPageContentSections.IN_PROGRESS),
  },
};

export const withUrlPrefix = (linkType: LinkType): LinkType => ({ ...linkType, href: getUrlPrefix() + linkType.href });

export const AppNavigation: React.FC = () => {
  const { user } = useAtlassianUser();
  const location = useLocation();
  const communityUser = {
    id: user?.account_id || '',
    publicName: user?.name || '',
    avatar: user?.picture || '',
  };

  const showChampions = FeatureGates.checkGate(CHAMPIONS_PAGE_FF) ?? false;
  const showAccountSwitcher = FeatureGates.checkGate(COMMUNITY_ACCOUNT_SWITCHER_FF) ?? false;
  const showNewTopNavigation = FeatureGates.checkGate(NEW_TOP_NAVIGATION_FF) ?? false;

  /* eslint-disable react-hooks/exhaustive-deps */
  const switchAccountUrl = useMemo(
    () => (showAccountSwitcher ? getSwitchAccountUrl() : ''),
    [location, showAccountSwitcher],
  );

  const loginUrl = useMemo(getLoginUrl, [location]);
  const logoutUrl = useMemo(getLogoutUrl, [location]);
  const signupUrl = useMemo(getSignUpUrl, [location]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <CommunityNavigation
      signupHref={signupUrl}
      currentApp={CommunityApp.LEARNING}
      isLoggedIn={!!user}
      user={communityUser}
      loginHref={loginUrl}
      logoutHref={logoutUrl}
      email={user?.email}
      isSticky={false}
      showChampions={showChampions}
      switchHref={switchAccountUrl}
      subMenuContentByApp={{
        [CommunityApp.LEARNING]: (
          <>
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.HOME)} type="subMenu" />
            {showNewTopNavigation ? (
              <NavbarSlideMenuItem title="On-demand learning" menuContent={<OnDemandMobileMenu />} />
            ) : (
              <NavBarExpandableItem
                isNested={true}
                headerContent={<span css={onDemandLearningHeaderStyles}>On-demand learning</span>}
                bodyContent={
                  <>
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.BROWSE_ALL)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.LEARNING_PATHS)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.PREP_FOR_CERT)} type="subMenu" />
                    <MenuItemWithAnalytics link={withUrlPrefix(LINKS.GROW_YOUR_CAREER)} type="subMenu" />
                  </>
                }
              />
            )}
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.LIVE_TRAINING)} type="subMenu" />
            <MenuItemWithAnalytics link={withUrlPrefix(LINKS.CERTIFICATIONS)} type="subMenu" />
            {user && <MenuItemWithAnalytics link={withUrlPrefix(LINKS.MY_LEARNING)} type="subMenu" />}
            {showNewTopNavigation && <SearchCatalogLink />}
          </>
        ),
      }}
      onAnalyticsSendEvent={(event, payload) => {
        sendMarketingUIEvent(payload, user?.account_id);
      }}
    />
  );
};

const SearchCatalogLink = (): React.ReactElement => (
  <div data-testid="search-catalog-link" css={browseAllLearningLinkStyles}>
    <MenuItemWithAnalytics
      link={withUrlPrefix(LINKS.CATALOG)}
      contentBefore={
        <span css={iconWrapperStyles}>
          <Icon type="search" size="medium" />
        </span>
      }
      type="subMenu"
    />
  </div>
);

const onDemandLearningHeaderStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const browseAllLearningLinkStyles = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: token('space.150'),
  margin: `${token('space.100')} 0`,
  paddingLeft: '36px',
  cursor: 'pointer',

  '&:hover a': {
    color: token('color.link'),
  },

  '&:active': {
    backgroundColor: token('color.background.accent.blue.subtlest'),
  },

  '& > span': {
    width: '100%',
  },

  li: {
    padding: 0,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '-0.08px',
    backgroundColor: 'unset',
  },
});

const iconWrapperStyles = css({
  marginRight: token('space.150'),
});
